import jsPDF from 'jspdf';
import { getReportContent } from './reportContentMapper';
import { questions } from '@/data/questions';

interface LeadData {
  name?: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
}

interface QuizData {
  score: number;
  quiz_band: 'low' | 'mid' | 'high';
  domain?: string;
  quiz_answers?: Record<string, any>;
  competitors?: string[];
  created_at: string;
}

export async function generateQuizReportPDF(
  leadData: LeadData,
  quizData: QuizData
): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  const primaryColor: [number, number, number] = [15, 23, 42]; // #0F172A
  const accentColor: [number, number, number] = 
    quizData.quiz_band === 'high' ? [16, 185, 129] : // green
    quizData.quiz_band === 'mid' ? [245, 158, 11] : // orange
    [239, 68, 68]; // red

  // Helper: Add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPos + requiredSpace > pageHeight - margin) {
      doc.addPage();
      yPos = margin;
      return true;
    }
    return false;
  };

  // Helper: Split text into lines
  const splitText = (text: string, maxWidth: number, fontSize: number = 10) => {
    doc.setFontSize(fontSize);
    return doc.splitTextToSize(text, maxWidth);
  };

  // Header
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 40, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('RAPORT LINKEDIN READINESS', margin, 20);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text('Kompleksowa analiza Twojej strategii LinkedIn', margin, 30);
  
  yPos = 50;

  // Personal data section (if available)
  if (leadData.name || leadData.email) {
    checkPageBreak(30);
    doc.setFillColor(250, 250, 250);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DANE UCZESTNIKA', margin + 5, yPos + 8);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let dataY = yPos + 15;
    if (leadData.name) {
      doc.text(`Imię i nazwisko: ${leadData.name}`, margin + 5, dataY);
      dataY += 5;
    }
    if (leadData.email) {
      doc.text(`Email: ${leadData.email}`, margin + 5, dataY);
      dataY += 5;
    }
    if (leadData.company && leadData.position) {
      doc.text(`${leadData.company} | ${leadData.position}`, margin + 5, dataY);
    }
    
    yPos += 30;
  }

  // Quiz summary
  checkPageBreak(35);
  doc.setFillColor(...accentColor);
  doc.rect(margin, yPos, contentWidth, 30, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const bandLabels = {
    low: 'SLEEPER',
    mid: 'CHALLENGER',
    high: 'DOMINACJA'
  };
  doc.text(`WYNIK: ${quizData.score}/10 - ${bandLabels[quizData.quiz_band]}`, margin + 5, yPos + 12);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  if (quizData.domain) {
    doc.text(`Domena: ${quizData.domain}`, margin + 5, yPos + 20);
  }
  doc.text(`Data analizy: ${new Date(quizData.created_at).toLocaleDateString('pl-PL')}`, margin + 5, yPos + 25);
  
  yPos += 40;

  // Report content
  const reportContent = getReportContent(quizData.quiz_band);
  
  // Header section
  checkPageBreak(30);
  doc.setTextColor(...primaryColor);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(reportContent.header.title, margin, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const headerLines = splitText(reportContent.header.description, contentWidth);
  headerLines.forEach((line: string) => {
    checkPageBreak(6);
    doc.text(line, margin, yPos);
    yPos += 5;
  });
  yPos += 5;

  // Statistics
  checkPageBreak(25);
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, yPos, contentWidth, 20, 'F');
  doc.setTextColor(...primaryColor);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('STATYSTYKI', margin + 5, yPos + 7);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  let statsY = yPos + 13;
  reportContent.stats.forEach((stat) => {
    doc.text(`• ${stat.label}: ${stat.value}`, margin + 5, statsY);
    statsY += 4;
  });
  yPos += 25;

  // Recommendations
  doc.setTextColor(...primaryColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  checkPageBreak(10);
  doc.text('REKOMENDACJE', margin, yPos);
  yPos += 8;

  reportContent.recommendations.forEach((rec, index) => {
    checkPageBreak(15);
    doc.setFillColor(...accentColor);
    doc.circle(margin + 3, yPos - 2, 2, 'F');
    doc.setTextColor(...primaryColor);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`${index + 1}. ${rec.title}`, margin + 8, yPos);
    yPos += 6;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const descLines = splitText(rec.description, contentWidth - 5);
    descLines.forEach((line: string) => {
      checkPageBreak(5);
      doc.text(line, margin + 8, yPos);
      yPos += 4;
    });
    yPos += 2;
    
    doc.setFont('helvetica', 'bold');
    doc.text('Jak to zrobić:', margin + 8, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    
    rec.steps.forEach((step, stepIndex) => {
      checkPageBreak(8);
      const stepLines = splitText(`${stepIndex + 1}) ${step}`, contentWidth - 15, 9);
      stepLines.forEach((line: string, lineIndex: number) => {
        checkPageBreak(5);
        doc.text(line, margin + 12, yPos);
        yPos += 4;
      });
    });
    yPos += 5;
  });

  // Advanced tips (Mid only)
  if (reportContent.advancedTips) {
    checkPageBreak(10);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('ZAAWANSOWANE TAKTYKI', margin, yPos);
    yPos += 8;
    
    reportContent.advancedTips.forEach((tip) => {
      checkPageBreak(12);
      doc.setFillColor(250, 250, 250);
      const tipHeight = splitText(tip.description, contentWidth - 10, 9).length * 4 + 10;
      doc.rect(margin, yPos - 4, contentWidth, tipHeight, 'F');
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(tip.title, margin + 5, yPos);
      yPos += 5;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const tipLines = splitText(tip.description, contentWidth - 10);
      tipLines.forEach((line: string) => {
        checkPageBreak(5);
        doc.text(line, margin + 5, yPos);
        yPos += 4;
      });
      yPos += 5;
    });
  }

  // Common mistakes (Low only)
  if (reportContent.commonMistakes) {
    checkPageBreak(10);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('NAJCZĘSTSZE BŁĘDY', margin, yPos);
    yPos += 8;
    
    reportContent.commonMistakes.forEach((mistake) => {
      checkPageBreak(12);
      doc.setFillColor(254, 242, 242);
      const mistakeHeight = splitText(mistake.description, contentWidth - 10, 9).length * 4 + 10;
      doc.rect(margin, yPos - 4, contentWidth, mistakeHeight, 'F');
      
      doc.setTextColor(220, 38, 38);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`⚠ ${mistake.title}`, margin + 5, yPos);
      yPos += 5;
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const mistakeLines = splitText(mistake.description, contentWidth - 10);
      mistakeLines.forEach((line: string) => {
        checkPageBreak(5);
        doc.text(line, margin + 5, yPos);
        yPos += 4;
      });
      yPos += 5;
    });
  }

  // Competitive advantages (High only)
  if (reportContent.competitiveAdvantages) {
    checkPageBreak(10);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('PRZEWAGI KONKURENCYJNE', margin, yPos);
    yPos += 8;
    
    reportContent.competitiveAdvantages.forEach((advantage) => {
      checkPageBreak(12);
      doc.setFillColor(240, 253, 244);
      const advHeight = splitText(advantage.description, contentWidth - 10, 9).length * 4 + 10;
      doc.rect(margin, yPos - 4, contentWidth, advHeight, 'F');
      
      doc.setTextColor(22, 163, 74);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`★ ${advantage.title}`, margin + 5, yPos);
      yPos += 5;
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      const advLines = splitText(advantage.description, contentWidth - 10);
      advLines.forEach((line: string) => {
        checkPageBreak(5);
        doc.text(line, margin + 5, yPos);
        yPos += 4;
      });
      yPos += 5;
    });
  }

  // Quick wins
  checkPageBreak(10);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('QUICK WINS - SZYBKIE DZIAŁANIA', margin, yPos);
  yPos += 8;
  
  reportContent.quickWins.forEach((win) => {
    checkPageBreak(15);
    doc.setFillColor(254, 252, 232);
    doc.rect(margin, yPos - 3, contentWidth, 16, 'F');
    
    doc.setTextColor(...primaryColor);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(`☐ ${win.title}`, margin + 5, yPos);
    yPos += 5;
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(win.description, margin + 10, yPos);
    yPos += 4;
    doc.setFont('helvetica', 'italic');
    doc.text(`Timeline: ${win.timeline} | Impact: ${win.impact}`, margin + 10, yPos);
    yPos += 8;
  });

  // Quiz details section
  doc.addPage();
  yPos = margin;
  
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('SZCZEGÓŁOWE ODPOWIEDZI QUIZU', margin, 18);
  
  yPos = 40;

  // Questions and answers
  if (quizData.quiz_answers) {
    questions.forEach((question, index) => {
      if (question.type === 'text') return;
      
      const answer = quizData.quiz_answers![question.id];
      if (!answer) return;

      checkPageBreak(20);
      
      doc.setTextColor(...primaryColor);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(`Q${index + 1}: ${question.question}`, margin, yPos);
      yPos += 6;
      
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      
      if (Array.isArray(answer)) {
        answer.forEach((ans: string) => {
          const option = question.options?.find(opt => opt.value === ans);
          if (option) {
            checkPageBreak(5);
            doc.text(`✓ ${option.label}`, margin + 5, yPos);
            yPos += 5;
          }
        });
      } else {
        const option = question.options?.find(opt => opt.value === answer);
        if (option) {
          doc.text(`✓ ${option.label}`, margin + 5, yPos);
          yPos += 5;
        }
      }
      yPos += 3;
    });
  }

  // Competitors
  if (quizData.competitors && quizData.competitors.length > 0) {
    checkPageBreak(20);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('WYBRANI KONKURENCI', margin, yPos);
    yPos += 7;
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    quizData.competitors.forEach((comp) => {
      checkPageBreak(5);
      doc.text(`• ${comp}`, margin + 5, yPos);
      yPos += 5;
    });
  }

  // Footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Strona ${i} z ${totalPages} | Wygenerowano: ${new Date().toLocaleDateString('pl-PL')}`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    );
  }

  // Generate filename
  const namePart = leadData.name 
    ? leadData.name.toLowerCase().replace(/\s+/g, '-') 
    : 'raport';
  const datePart = new Date().toISOString().split('T')[0];
  const filename = `linkedin-readiness-${namePart}-${datePart}.pdf`;

  // Save PDF
  doc.save(filename);
}
